import React, { useEffect, useState } from "react";
import { CiShop, CiUser } from "react-icons/ci";
import { HiOutlineTrash, HiOutlineShoppingCart } from "react-icons/hi";
import CartProductCard from "./CartProductCard";
import { useNavigate } from "react-router-dom";

import {
  getCart,
  updateCartItem,
  removeCartItem,
  checkoutCart,
} from "../../api/cartApi";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";

export default function CartPage() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate(); // Navigation hook

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setLoadError("");
      try {
        const res = await getCart();
        // A failed load must not look like an empty cart
        if (res?.success === false) {
          if (mounted) setLoadError(res.message || "We couldn't load your cart.");
          return;
        }
        const items =
          res?.cart_items ||
          res?.cartItems ||
          res?.items ||
          res?.result?.cart_items ||
          [];
        if (mounted) setCartItems(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error("Failed to load cart:", err);
        if (mounted) setLoadError("We couldn't load your cart.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => (mounted = false);
  }, [reloadKey]);

  function getItemPrice(item) {
    const priceCandidates = [
      item.price,
      item.unit_price,
      item.product_price,
      item.product?.discount_price,
      item.product?.product_price,
      item.product?.discount_price,
    ];
    for (const p of priceCandidates) {
      const n = Number(p);
      if (!Number.isNaN(n)) return n;
    }
    return 0;
  }

  function computeSubtotal() {
    return cartItems.reduce((sum, it) => {
      const qty = Number(it.quantity || it.qty || it.count) || 1;
      return sum + getItemPrice(it) * qty;
    }, 0);
  }

  async function handleUpdateQuantity(item, newQty) {
    if (newQty < 1) return;
    const prev = cartItems;
    const updated = prev.map((it) =>
      it.id === item.id ? { ...it, quantity: newQty } : it
    );
    setCartItems(updated);
    try {
      await updateCartItem(item.id, newQty, item.variant_id || 0);
    } catch (err) {
      setCartItems(prev);
      alert("Failed to update quantity");
    }
  }

  async function handleRemove(itemId) {
    const prev = cartItems;
    setCartItems(prev.filter((it) => it.id !== itemId));
    try {
      await removeCartItem(itemId);
    } catch (err) {
      setCartItems(prev);
      alert("Failed to remove item");
    }
  }

  // async function handleCheckout() {
  //   const payload = {
  //     payment_method: "card",
  //     payment_method_id: "pm_mock_123",
  //     shipping_address: {
  //       name: "John Doe",
  //       address_line_1: "123 Street",
  //       address_line_2: "",
  //       city: "City",
  //       state: "State",
  //       postal_code: "000000",
  //       country: "Country",
  //       phone: "0000000000",
  //     },
  //   };

  //   try {
  //     const res = await checkoutCart(payload.payment_method, payload.payment_method_id, payload.shipping_address);
  //     alert(res?.message || "Checkout attempted (mock)");
  //   } catch (err) {
  //     console.error("Checkout failed:", err);
  //     alert("Checkout failed");
  //   }
  // }

  // Same rule as the API's checkout pricing: one delivery fee per cart item (not per unit), taken from the product
  const deliveryFee = cartItems.reduce(
    (sum, it) => sum + (Number(it.delivery_fee ?? it.product?.delivery_fee) || 0),
    0
  );
  const subtotal = computeSubtotal();
  const total = subtotal + deliveryFee;
  const currencySymbol = cartItems[0]?.seller?.currency_symbol || cartItems[0]?.product?.seller?.currency_symbol || "$";

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />

        <div className="">
          <div className="bg-white rounded-[24px] md:rounded-[35px] p-3 md:p-6 ">
            {/* <h2 className="font-bold text-2xl">Checkout</h2> */}
            <div className="mt-6 grid grid-cols-1  lg:grid-cols-3 gap-6">
              {/* Product list (left) — span 2 columns on lg */}
              <div className="lg:col-span-2 border p-2 rounded-md space-y-4">
                {loading ? (
                  <div className="p-4">Loading cart...</div>
                ) : loadError ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                    <p className="text-sm text-red-600">{loadError}</p>
                    <button
                      onClick={() => setReloadKey((key) => key + 1)}
                      className="mt-4 bg-[#095228] hover:bg-[#0b6a33] text-white rounded-full px-6 py-2.5 font-semibold"
                    >
                      Try Again
                    </button>
                  </div>
                ) : cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                    <div className="w-20 h-20 rounded-full bg-[#095228]/10 flex items-center justify-center mb-4">
                      <HiOutlineShoppingCart className="w-10 h-10 text-[#095228]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Your cart is empty
                    </h3>
                    <p className="text-sm text-gray-500 mt-2 max-w-sm">
                      Looks like you haven't added anything yet. Explore the
                      marketplace to find something you like.
                    </p>
                    <button
                      onClick={() => navigate("/marketplace")}
                      className="mt-6 bg-[#095228] hover:bg-[#0b6a33] text-white rounded-full px-6 py-2.5 font-semibold"
                    >
                      Browse Marketplace
                    </button>
                  </div>
                ) : (
                  cartItems.map((item) => {
                    const prod = item.product || item.product_data || item;
                    const name =
                      prod?.product_name ||
                      prod?.title ||
                      prod?.name ||
                      "Product";
                    const img =
                      (prod?.product_media &&
                        prod.product_media[0] &&
                        prod.product_media[0].url) ||
                      prod?.product_media_url ||
                      prod?.image ||
                      "/image/shoeimg.png";
                    const price = getItemPrice(item);
                    const qty =
                      Number(item.quantity || item.qty || item.count) || 1;
                    return (
                      <CartProductCard
                        key={item.id}
                        item={item}
                        image={img}
                        title={name}
                        unitPrice={price}
                        quantity={qty}
                        onQuantityChange={(newQty) =>
                          handleUpdateQuantity(item, newQty)
                        }
                        onRemove={() => handleRemove(item.id)}
                      />
                    );
                  })
                )}
              </div>

              {/* Order summary (right) */}
              <div className="bg-white   ">
                <div className="border shadow-sm  rounded-lg p-4 ">
                  <h3 className="font-semibold text-lg">Order Summary</h3>
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span>
                      <span>{currencySymbol}{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Delivery fee</span>
                      <span>{currencySymbol}{deliveryFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax</span>
                      <span>Calculated at checkout</span>
                    </div>
                    <div className="border-t pt-3 mt-3 flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>{currencySymbol}{total.toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate("/addshopperaddress")}
                    disabled={loading || cartItems.length === 0}
                    className={`w-full mt-6 text-white rounded-lg py-3 font-semibold ${
                      loading || cartItems.length === 0
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-[#2B4E3E] hover:bg-[#244033]"
                    }`}
                  >
                    Pay Now{" "}
                  </button>

                  {/* <button className="w-full mt-3 border border-[#2B4E3E] text-[#2B4E3E] rounded-lg py-2 font-medium">
                    Continue Shopping
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
